import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react'
import { doc, getDoc } from 'firebase/firestore'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { InstallAppPrompt } from '@/components/pwa/InstallAppPrompt'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Spinner } from '@/components/ui/spinner'
import { useAuth } from '@/hooks/use-auth'
import {
  BIDDLE_APP_STORE_URL,
  campaignEvent,
  campaignSearchPrefix,
  isValidCampaignCode,
  normalizeCampaignCode,
  resolveCampaignCode,
  submitCampaignWaitlist,
  type CampaignResolveResult,
} from '@/lib/campaign'
import { formatAddressDisplay } from '@/lib/address'
import { callableErrorMessage } from '@/lib/callable-errors'
import { searchPropertiesByPrefix, submitLetterOwnershipClaim, type PropertySuggestion } from '@/lib/callables'
import { LetterPropertyPreviewModal } from '@/components/claim/LetterPropertyPreviewModal'
import { db } from '@/lib/firebase'
import { openSignIn } from '@/lib/signin-navigation'
import { openVerifyIdentity } from '@/lib/verify-identity-navigation'
import { isIOSDevice } from '@/lib/pwa'
import { cn } from '@/lib/utils'

type Phase =
  | 'enter'
  | 'resolving'
  | 'confirm'
  | 'disambiguate'
  | 'noMatch'
  | 'kycGate'
  | 'verifyLater'
  | 'submitting'
  | 'done'
  | 'error'

function onceKey(code: string, event: string): string {
  return `campaign:${event}:${code}`
}

function fireOnce(code: string, event: Parameters<typeof campaignEvent>[1]): void {
  try {
    const key = onceKey(code, String(event))
    if (sessionStorage.getItem(key)) return
    sessionStorage.setItem(key, '1')
  } catch {
    // Private mode — still fire; backend is set-only-if-empty.
  }
  campaignEvent(code, event)
}

export function GotALetterWizard({
  initialCode,
  syncUrl = false,
  hideIntro = false,
  onComplete,
  onPropertyAddress,
  onResolveFinished,
}: {
  /** Prefill / auto-resolve (e.g. from `/o/:code`). */
  initialCode?: string
  /** When true, Continue updates the URL to `/o/:code` (full-page journey). */
  syncUrl?: boolean
  /** Hide the page heading when embedded in a modal / marketing CTA that already has a title. */
  hideIntro?: boolean
  onComplete?: (propertyId: string) => void
  /** Directory property `fullAddress` once the letter code is matched (for parent hero copy). */
  onPropertyAddress?: (fullAddress: string) => void
  /** Fired when auto-resolve / lookup finishes (success, no-match, or error). */
  onResolveFinished?: () => void
}) {
  const navigate = useNavigate()
  const location = useLocation()
  const { user, profile, isGuest, isLoading } = useAuth()

  const [codeInput, setCodeInput] = useState(initialCode ?? '')
  const [phase, setPhase] = useState<Phase>('enter')
  const [info, setInfo] = useState<CampaignResolveResult | null>(null)
  const [matches, setMatches] = useState<PropertySuggestion[]>([])
  const [selected, setSelected] = useState<PropertySuggestion | null>(null)
  const [confirmed, setConfirmed] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [claimMessage, setClaimMessage] = useState<string | null>(null)
  const [previewOpen, setPreviewOpen] = useState(false)
  const [verifyLaterBusy, setVerifyLaterBusy] = useState(false)
  const [verifyLaterMessage, setVerifyLaterMessage] = useState<string | null>(null)

  const onPropertyAddressRef = useRef(onPropertyAddress)
  const onResolveFinishedRef = useRef(onResolveFinished)
  const autoResolvedCodeRef = useRef<string | null>(null)
  useEffect(() => {
    onPropertyAddressRef.current = onPropertyAddress
  }, [onPropertyAddress])

  useEffect(() => {
    onResolveFinishedRef.current = onResolveFinished
  }, [onResolveFinished])

  const resolve = useCallback(async (raw: string) => {
    const code = normalizeCampaignCode(raw)
    if (!code) {
      setPhase('error')
      setError('That code doesn’t look right. Use the 7-character code from your letter.')
      onResolveFinishedRef.current?.()
      return
    }

    setPhase('resolving')
    setError(null)
    setSelected(null)
    setConfirmed(false)
    setMatches([])

    try {
      const resolved = await resolveCampaignCode(code)
      if (!resolved.found) {
        setPhase('error')
        setError('We couldn’t find a property for that code. Double-check the letter.')
        return
      }
      setInfo(resolved)
      fireOnce(code, 'web_land')

      // Preferred path: campaign piece already linked to a property doc.
      const linkedId =
        typeof resolved.propertyId === 'string' && resolved.propertyId.trim()
          ? resolved.propertyId.trim()
          : null
      if (linkedId) {
        const snap = await getDoc(doc(db, 'properties', linkedId))
        if (snap.exists()) {
          const data = snap.data() || {}
          const fullAddress =
            typeof data.fullAddress === 'string' && data.fullAddress
              ? data.fullAddress
              : [resolved.addressLine1, resolved.suburb, resolved.postcode]
                  .filter(Boolean)
                  .join(', ')
          const property: PropertySuggestion = {
            id: linkedId,
            fullAddress,
            suburb: typeof data.suburb === 'string' ? data.suburb : resolved.suburb ?? undefined,
            state: typeof data.state === 'string' ? data.state : undefined,
            postCode:
              typeof data.postCode === 'string'
                ? data.postCode
                : typeof data.postcode === 'string'
                  ? data.postcode
                  : resolved.postcode ?? undefined,
          }
          setMatches([property])
          setSelected(property)
          onPropertyAddressRef.current?.(property.fullAddress)
          setPhase('confirm')
          return
        }
      }

      // Legacy fallback for pieces not yet linked.
      const prefix = campaignSearchPrefix(resolved.addressLine1 ?? '')
      const search = prefix
        ? await searchPropertiesByPrefix(prefix, 10)
        : { properties: [] as PropertySuggestion[] }
      const properties = search.properties ?? []

      const exact = properties.filter((p) => {
        const address = p.fullAddress
        const suburbOk =
          !resolved.suburb || address.toLowerCase().includes(resolved.suburb.toLowerCase())
        const postcodeOk = !resolved.postcode || address.includes(resolved.postcode)
        return suburbOk && postcodeOk
      })

      if (exact.length === 1) {
        const property = exact[0]!
        setMatches(exact)
        setSelected(property)
        onPropertyAddressRef.current?.(property.fullAddress)
        setPhase('confirm')
      } else if (exact.length > 1) {
        setMatches(exact)
        setPhase('disambiguate')
      } else if (properties.length > 0) {
        setMatches(properties)
        setPhase('disambiguate')
      } else {
        setPhase('noMatch')
      }
    } catch (e) {
      setPhase('error')
      setError(e instanceof Error ? e.message : 'Could not reach Biddle. Try again.')
    } finally {
      onResolveFinishedRef.current?.()
    }
  }, [])

  useEffect(() => {
    if (!initialCode) return
    if (!isValidCampaignCode(initialCode)) {
      setPhase('error')
      setError('That code doesn’t look right. Use the 7-character code from your letter.')
      onResolveFinishedRef.current?.()
      return
    }
    const normalized = normalizeCampaignCode(initialCode)
    if (!normalized) return
    // Avoid re-lookup when parent re-renders or auth overlays open/close.
    if (autoResolvedCodeRef.current === normalized) return
    autoResolvedCodeRef.current = normalized
    setCodeInput(initialCode)
    void resolve(initialCode)
  }, [initialCode, resolve])

  // account_created after /signin round-trip (new Firebase user only).
  useEffect(() => {
    if (!info?.code || !user || isGuest || isLoading) return
    const created = user.metadata.creationTime
    const last = user.metadata.lastSignInTime
    if (!created || !last || created !== last) return
    fireOnce(info.code, 'account_created')
  }, [info?.code, user, isGuest, isLoading])

  const submitClaimRef = useRef<(() => Promise<void>) | null>(null)

  // kyc_completed only after kyc_started in this browser session (avoids false stamps).
  useEffect(() => {
    if (!info?.code || !profile?.isIdentityVerified) return
    let started = false
    try {
      started = sessionStorage.getItem(onceKey(info.code, 'kyc_started')) === '1'
    } catch {
      started = phase === 'kycGate'
    }
    if (!started) return
    fireOnce(info.code, 'kyc_completed')
    if (phase === 'kycGate' && selected && confirmed) {
      void submitClaimRef.current?.()
    }
  }, [profile?.isIdentityVerified, info?.code, phase, selected, confirmed])

  async function submitClaim() {
    if (!selected || !info?.code) return
    if (!confirmed) {
      setError('Confirm you are the owner of this property.')
      return
    }

    fireOnce(info.code, 'register_start')

    if (!user || isGuest) {
      setPreviewOpen(false)
      openSignIn(navigate, location, `/o/${info.code}`)
      return
    }
    if (!user.emailVerified && !profile?.emailVerified) {
      setPreviewOpen(false)
      navigate(`/verify-email?next=${encodeURIComponent(`/o/${info.code}`)}`)
      return
    }
    if (!profile?.isIdentityVerified) {
      setPreviewOpen(false)
      setPhase('kycGate')
      return
    }

    setPhase('submitting')
    setError(null)
    try {
      await submitLetterOwnershipClaim({
        campaignCode: info.code,
        propertyID: selected.id,
        ownerConfirmed: true,
      })
      fireOnce(info.code, 'claim_submitted')
      setClaimMessage(
        'Claim submitted. If you’re already verified, ownership is granted shortly; otherwise finish identity verification.',
      )
      setPhase('done')
      setPreviewOpen(false)
      onComplete?.(selected.id)
    } catch (e) {
      setPhase('confirm')
      setError(callableErrorMessage(e))
    }
  }

  submitClaimRef.current = submitClaim

  function startKyc() {
    if (!info?.code) return
    fireOnce(info.code, 'kyc_started')
    openVerifyIdentity(navigate, location, `/o/${info.code}`, 'letter-claim')
  }

  async function verifyLater() {
    if (!info?.code) return
    if (!user || isGuest || !user.email) {
      setPreviewOpen(false)
      openSignIn(navigate, location, `/o/${info.code}`)
      return
    }
    setVerifyLaterBusy(true)
    setVerifyLaterMessage(null)
    const ok = await submitCampaignWaitlist({
      code: info.code,
      ownerConfirmed: confirmed || phase === 'noMatch',
      email: user.email,
      name: profile?.displayName ?? undefined,
      consent: true,
    })
    setVerifyLaterBusy(false)
    setPhase('verifyLater')
    setVerifyLaterMessage(
      ok
        ? phase === 'noMatch' || !selected
          ? 'Thanks — we’ve saved your details. Our team will help link your letter to the right property.'
          : 'Thanks — we’ve saved your details. You can verify your identity anytime from Settings and finish claiming this property.'
        : 'We couldn’t save a notify-me request right now. Your account is still signed in — you can verify later from Settings.',
    )
  }

  function preferIosApp() {
    if (!info?.code) return
    fireOnce(info.code, 'app_redirect')
    window.open(BIDDLE_APP_STORE_URL, '_blank', 'noopener,noreferrer')
  }

  const showPreferIos =
    Boolean(info?.code) &&
    (info?.platform === 'ios' || info?.isIOS === true || isIOSDevice()) &&
    (phase === 'confirm' || phase === 'disambiguate' || phase === 'kycGate')

  const addressLine = selected
    ? formatAddressDisplay(selected.fullAddress)
    : ''

  // On the campaign landing, the parent already gates on auth readiness — don't
  // blank the claim card with a second spinner after the hero has appeared.
  const showAuthSpinner =
    isLoading &&
    !(
      hideIntro &&
      (phase === 'confirm' ||
        phase === 'disambiguate' ||
        phase === 'noMatch' ||
        phase === 'kycGate' ||
        phase === 'verifyLater' ||
        phase === 'error' ||
        phase === 'done' ||
        selected != null)
    )

  if (showAuthSpinner) {
    return (
      <div className={cn('flex items-center justify-center', hideIntro ? 'py-8' : 'min-h-[40vh]')}>
        <Spinner />
      </div>
    )
  }

  const enterForm = (
    <div className="space-y-4">
      <div className="space-y-2">
        <Label htmlFor="letter-code">Letter code</Label>
        <Input
          id="letter-code"
          value={codeInput}
          onChange={(e) => setCodeInput(e.target.value)}
          placeholder="e.g. yc4465v"
          autoCapitalize="off"
          autoCorrect="off"
          className="h-12"
        />
        <p className="text-xs text-muted">7 characters under the QR code.</p>
      </div>
      {error ? <p className="text-sm text-danger">{error}</p> : null}
      <Button
        variant="primary"
        className="w-full"
        onClick={() => {
          const normalized = normalizeCampaignCode(codeInput)
          if (normalized && syncUrl) {
            navigate(`/o/${normalized}`)
            return
          }
          void resolve(codeInput)
        }}
      >
        Continue
      </Button>
    </div>
  )

  return (
    <div className={cn('flex w-full flex-col', hideIntro ? 'gap-4' : 'gap-6')}>
      {!hideIntro ? (
        <div>
          <h1 className="text-2xl font-bold text-navy">Got a letter?</h1>
          <p className="mt-2 text-sm text-muted">Enter your letter code to claim ownership.</p>
        </div>
      ) : null}

      {(phase === 'enter' || phase === 'error') &&
        (hideIntro ? enterForm : <Card className="p-5">{enterForm}</Card>)}

      {phase === 'resolving' || phase === 'submitting' ? (
        <div
          className={cn(
            'flex flex-col items-center gap-3',
            hideIntro ? 'py-6' : 'py-16',
          )}
        >
          <Spinner />
          <p className="text-sm text-muted">
            {phase === 'resolving' ? 'Looking up your letter…' : 'Submitting claim…'}
          </p>
        </div>
      ) : null}

      {(phase === 'confirm' || phase === 'disambiguate') && info ? (
        <ConfirmPanel hideChrome={hideIntro}>
          {!hideIntro ? (
            <div className="space-y-1">
              <h3 className="text-base font-semibold text-navy">Confirm your property</h3>
              <p className="text-sm text-muted">{addressLine || '—'}</p>
            </div>
          ) : null}

          <div className={cn(hideIntro ? 'space-y-3' : 'space-y-4')}>
            {phase === 'disambiguate' ? (
              <ul className="space-y-2">
                {matches.map((m) => (
                  <li key={m.id}>
                    <button
                      type="button"
                      className={`w-full rounded-xl border px-3 py-3 text-left text-sm ${
                        selected?.id === m.id
                          ? 'border-biddle-blue bg-blue-50'
                          : 'border-border hover:border-biddle-blue'
                      }`}
                      onClick={() => {
                        setSelected(m)
                        onPropertyAddress?.(m.fullAddress)
                        setPhase('confirm')
                      }}
                    >
                      {formatAddressDisplay(m.fullAddress)}
                    </button>
                  </li>
                ))}
              </ul>
            ) : null}

            {selected ? (
              <>
                <p
                  className={cn(
                    'rounded-xl bg-surface font-medium text-navy',
                    hideIntro ? 'px-3 py-2.5 text-sm' : 'px-3 py-3 text-sm',
                  )}
                >
                  {formatAddressDisplay(selected.fullAddress)}
                </p>
                <label className="flex items-start gap-2.5 text-sm leading-snug">
                  <input
                    type="checkbox"
                    className="mt-0.5"
                    checked={confirmed}
                    onChange={(e) => setConfirmed(e.target.checked)}
                  />
                  <span>I confirm I am the legal owner of this property.</span>
                </label>
                {error ? <p className="text-sm text-danger">{error}</p> : null}
                <Button
                  variant="primary"
                  className="w-full"
                  disabled={!confirmed}
                  onClick={() => void submitClaim()}
                >
                  Claim ownership
                </Button>
                {hideIntro ? (
                  <button
                    type="button"
                    className="w-full text-center text-sm font-medium text-biddle-blue underline-offset-2 hover:underline"
                    onClick={() => setPreviewOpen(true)}
                  >
                    See how your property appears
                  </button>
                ) : (
                  <p className="text-center text-xs text-muted">
                    There’s no cost and no obligation. You choose whether to respond to any interest.
                  </p>
                )}
                {showPreferIos ? (
                  <button
                    type="button"
                    className="w-full text-center text-xs text-muted underline-offset-2 hover:underline"
                    onClick={preferIosApp}
                  >
                    Prefer the Biddle iOS app?
                  </button>
                ) : null}
              </>
            ) : null}
          </div>
        </ConfirmPanel>
      ) : null}

      {phase === 'kycGate' && info ? (
        <ConfirmPanel hideChrome={hideIntro}>
          <div className="space-y-1">
            <h3 className="font-semibold text-navy">Verify your identity</h3>
            <p className="text-sm text-muted">
              Ownership claims need a quick identity check. You can verify now, or continue later —
              your account and letter code stay linked.
            </p>
          </div>
          {error ? <p className="text-sm text-danger">{error}</p> : null}
          <Button variant="primary" className="w-full" onClick={startKyc}>
            Verify identity
          </Button>
          <Button
            variant="outline"
            className="w-full"
            disabled={verifyLaterBusy}
            onClick={() => void verifyLater()}
          >
            {verifyLaterBusy ? 'Saving…' : 'Verify later'}
          </Button>
          {showPreferIos ? (
            <button
              type="button"
              className="w-full text-center text-xs text-muted underline-offset-2 hover:underline"
              onClick={preferIosApp}
            >
              Prefer the Biddle iOS app?
            </button>
          ) : null}
        </ConfirmPanel>
      ) : null}

      {phase === 'verifyLater' ? (
        <ConfirmPanel hideChrome={hideIntro}>
          <div className="space-y-1">
            <h3 className="font-semibold text-navy">You’re all set for now</h3>
            <p className="text-sm text-muted">
              {verifyLaterMessage ??
                'Come back anytime to verify your identity and finish claiming this property.'}
            </p>
          </div>
          <Button variant="primary" className="w-full" onClick={startKyc}>
            Verify identity now
          </Button>
          <Button variant="outline" className="w-full" asChild>
            <Link to="/settings">Go to Settings</Link>
          </Button>
        </ConfirmPanel>
      ) : null}

      {hideIntro && selected ? (
        <LetterPropertyPreviewModal
          open={previewOpen}
          propertyId={selected.id}
          confirmed={confirmed}
          onConfirmedChange={setConfirmed}
          onClaim={() => void submitClaim()}
          onClose={() => setPreviewOpen(false)}
          claiming={phase === 'submitting'}
          error={error}
        />
      ) : null}

      {phase === 'noMatch' && info ? (
        <ConfirmPanel hideChrome={hideIntro}>
          <div className="space-y-1">
            <h3 className="font-semibold text-navy">We couldn’t link this letter yet</h3>
            <p className="text-sm text-muted">
              We found your letter
              {info.addressLine1 ? <> for {info.addressLine1}</> : null}, but it’s not linked to a
              directory property yet. Leave your details and we’ll help you finish, or email{' '}
              <a className="text-biddle-blue underline" href="mailto:support@biddle.io">
                support@biddle.io
              </a>{' '}
              with code <span className="font-medium text-navy">{info.code}</span>.
            </p>
          </div>
          {error ? <p className="text-sm text-danger">{error}</p> : null}
          <Button
            variant="primary"
            className="w-full"
            disabled={verifyLaterBusy}
            onClick={() => {
              setConfirmed(true)
              void verifyLater()
            }}
          >
            {verifyLaterBusy ? 'Saving…' : 'Notify me when ready'}
          </Button>
          <Button variant="outline" onClick={() => setPhase('enter')}>
            Try another code
          </Button>
        </ConfirmPanel>
      ) : null}

      {phase === 'done' && !hideIntro ? (
        <Card>
          <CardHeader>
            <CardTitle>Claim received</CardTitle>
            <CardDescription>{claimMessage}</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex flex-wrap gap-2">
              <Button asChild variant="primary">
                <Link
                  to={selected ? `/properties/${selected.id}/biddles` : '/properties'}
                  onClick={() => selected && onComplete?.(selected.id)}
                >
                  View my property&apos;s Biddles
                </Link>
              </Button>
              {!profile?.isIdentityVerified ? (
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    if (selected) onComplete?.(selected.id)
                    openVerifyIdentity(
                      navigate,
                      location,
                      info?.code ? `/o/${info.code}` : '/settings',
                      'letter-claim',
                    )
                  }}
                >
                  Verify identity
                </Button>
              ) : null}
            </div>
            <InstallAppPrompt force compact />
          </CardContent>
        </Card>
      ) : null}
    </div>
  )
}

function ConfirmPanel({
  hideChrome,
  children,
}: {
  hideChrome: boolean
  children: ReactNode
}) {
  if (hideChrome) {
    return <div className="space-y-4">{children}</div>
  }
  return <Card className="space-y-4 p-5">{children}</Card>
}
